<template>
  <div class="filial-certificado-frame">
    <div class="d-flex justify-content-between align-items-center mb-2">
      <span class="text-body-secondary small">
        <i class="bi bi-shield-lock-fill me-1"></i>
        Certificado Digital A1 (e-CNPJ) — 1 ativo por filial
      </span>
      <button
        type="button"
        class="btn btn-sm btn-primary"
        :disabled="!idFilial"
        :title="!idFilial ? 'Salve a filial primeiro para adicionar o certificado' : 'Adicionar Certificado'"
        @click="abrirUpload"
      >
        <i class="bi bi-plus-lg me-1"></i>Adicionar
      </button>
    </div>

    <div v-if="loading" class="text-center p-4">
      <div class="spinner-border text-primary" role="status"><span class="visually-hidden">A carregar...</span></div>
    </div>

    <AdminTable
      v-else
      :columns="colunas"
      :data="lista"
      :loading="loading"
      empty-message="Nenhum certificado carregado para esta filial."
      row-key="idFilialCertificado"
      v-model:sort-field="sortField"
      v-model:sort-order="sortOrder"
    >
      <template #id="{ row }">
        <span class="text-muted fw-medium">#{{ row.idFilialCertificado }}</span>
      </template>
      <template #arquivo="{ row }">
        <div class="fw-bold text-body-emphasis">
          <i class="bi bi-file-earmark-lock-fill text-secondary me-2"></i>{{ row.nomeArquivo }}
        </div>
      </template>
      <template #vencimento="{ row }">
        <span :class="{ 'text-danger fw-bold': isVencido(row.dataVencimento), 'text-warning text-dark fw-bold': isVencendo(row.dataVencimento) }">
          {{ formatarDataBR(row.dataVencimento) }}
          <span v-if="isVencido(row.dataVencimento)" class="badge bg-danger ms-2">Expirado</span>
          <i v-if="isVencendo(row.dataVencimento) && !isVencido(row.dataVencimento)" class="bi bi-exclamation-triangle-fill ms-1" title="Vence em menos de 30 dias!"></i>
        </span>
      </template>
      <template #status="{ row }">
        <span class="badge" :class="row.isAtivo ? 'bg-success' : 'bg-secondary'">{{ row.isAtivo ? 'Em Uso' : 'Inativo/Histórico' }}</span>
      </template>
      <template #acoes="{ row }">
        <button class="btn-icon text-danger" @click="confirmarExclusao(row)" title="Excluir Definitivamente">
          <i class="bi bi-trash-fill"></i>
        </button>
      </template>
    </AdminTable>

    <ConfirmationModal
      ref="modalExclusao"
      titulo="Excluir Certificado"
      message="Tem certeza que deseja excluir este certificado do cofre? As comunicações com o Siscomex poderão falhar se não houver outro ativo."
      btn-confirmar-texto="Sim, Excluir"
      btn-cancelar-texto="Cancelar"
      :is-danger="true"
      @confirmar="executarExclusao"
    />

    <FilialCertificadoModal
      v-model="modalUploadOpen"
      :id-filial="idFilial"
      @salvo="onSalvo"
      @show-notification="(p) => emit('show-notification', p)"
    />
  </div>
</template>

<script setup>
import { ref, watch, onMounted } from 'vue'
import api from '@/services/api'
import AdminTable from '@/components/base/grid/AdminTable.vue'
import ConfirmationModal from '@/components/base/modal/ConfirmationModal.vue'
import FilialCertificadoModal from '@/components/organizacoes/FilialCertificadoModal.vue'
import { dataHojeBR, formatarDataBR, isVencido } from '@/utils/useDataBR'

const props = defineProps({
  idFilial: { type: Number, default: 0 }
})

const emit = defineEmits(['show-notification'])

const lista = ref([])
const loading = ref(false)
const modalExclusao = ref(null)
const certificadoParaExcluir = ref(null)
const modalUploadOpen = ref(false)

const sortField = ref('')
const sortOrder = ref('')

const colunas = [
  { field: 'idFilialCertificado', label: 'ID', width: 80, sortable: true, slotName: 'id' },
  { field: 'nomeArquivo', label: 'Ficheiro', sortable: true, slotName: 'arquivo' },
  { field: 'cnpjCpf', label: 'CNPJ Vinculado', sortable: true, class: 'font-monospace' },
  { field: 'dataVencimento', label: 'Vencimento (UTC)', sortable: true, slotName: 'vencimento' },
  { field: 'isAtivo', label: 'Status Físico', width: 150, align: 'center', sortable: true, slotName: 'status' },
  { field: 'acoes', label: 'Ações', width: 100, align: 'right', slotName: 'acoes', resizable: false, sortable: false }
]

const buscarDados = async () => {
  if (!props.idFilial) {
    lista.value = []
    return
  }

  loading.value = true
  try {
    const response = await api.get(`/FilialCertificados?idFilial=${props.idFilial}`)
    lista.value = response.data
  } catch (error) {
    emit('show-notification', { message: 'Erro ao carregar certificados.', type: 'error' })
  } finally {
    loading.value = false
  }
}

const abrirUpload = () => {
  if (!props.idFilial) return
  modalUploadOpen.value = true
}

const onSalvo = async () => {
  modalUploadOpen.value = false
  await buscarDados()
}

const confirmarExclusao = (cert) => {
  certificadoParaExcluir.value = cert
  modalExclusao.value?.show()
}

const executarExclusao = async () => {
  try {
    await api.delete(`/FilialCertificados/${certificadoParaExcluir.value.idFilialCertificado}`)
    emit('show-notification', { message: 'Certificado excluído com sucesso.', type: 'success' })
    await buscarDados()
  } catch (error) {
    const data = error.response?.data
    const msg = data?.message || data?.title || 'Erro ao excluir certificado.'
    emit('show-notification', { message: msg, type: 'error' })
  } finally {
    if (modalExclusao.value && modalExclusao.value.hide) {
      modalExclusao.value.hide()
    }
  }
}

const isVencendo = (dataStr) => {
  if (!dataStr) return false
  const hojeParts = dataHojeBR().split('-')
  const hoje = new Date(Number(hojeParts[0]), Number(hojeParts[1]) - 1, Number(hojeParts[2]))
  const dataVenc = new Date(dataStr + 'T00:00:00Z')
  const diffTime = Math.abs(dataVenc - hoje)
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
  return diffDays <= 30
}

watch(() => props.idFilial, (novo) => {
  if (novo) buscarDados()
  else lista.value = []
})

onMounted(() => { buscarDados() })

defineExpose({ buscarDados })
</script>

<style scoped>
.btn-icon { background: none; border: none; color: #6c7d8e; padding: 0.4rem; transition: 0.2s; }
.btn-icon:hover { color: var(--bs-body-emphasis); }
</style>
